import { Suspense, lazy } from 'react';
import { createBrowserRouter } from 'react-router-dom';
import { LoginRequired, SuperAdminRequired } from './middlewares';
import * as path from './paths';
import { IChildrenProps } from 'interfaces/general.interface';

// LAYOUTS
const MainLayout = lazy(() => import('../views/layouts/mainLayout/MainLayout'));
const AuthenticationLayout = lazy(() => import('../views/layouts/authLayout/AuthenticationLayout'));
const AdminLayout = lazy(() => import('views/layouts/adminLayout/AdminLayout'));
const AccountLayout = lazy(() => import('../views/layouts/accountLayout/AccountLayout'));
const ClientLayout = lazy(() => import('../views/layouts/clientLayout/ClientLayout'));
const CatalogUploadsLayout = lazy(() => import('../views/layouts/catalogUploadsLayout/CatalogUploadsLayout'));
//// PAGES
// Main Pages
const DashboardSalePage = lazy(() => import('views/pages/main/dashboards/dashboardSalePage'));
const DashboardStockPage = lazy(() => import('views/pages/main/dashboards/dashboardStockPage/DashboardStockPage'));
const DashboardPredictionPage = lazy(() => import('views/pages/main/dashboards/dashboardPredictionPage'));
const RegisterBrand = lazy(() => import('views/pages/main/registerBrandPage/RegisterBrandPage'));
const ThanksForRegister = lazy(() => import('views/pages/main/thanksForRegister/ThanksForRegister'));
const CatalogPage = lazy(() => import('views/pages/main/catalog/catalogPage/CatalogPage'));
const MultimediaPage = lazy(() => import('views/pages/main/multimediaPage'));
const MultimediaVideosPage = lazy(() => import('views/pages/main/multimediaPage/multimediaVideos'));
const MultimediaImagesPage = lazy(() => import('views/pages/main/multimediaPage/multimediaImages'));
const MultimediaBannersPage = lazy(() => import('views/pages/main/multimediaPage/multimediaBanners'));
const ClientsPage = lazy(() => import('views/pages/main/clientsPage/clientsPage/ClientsPage'));
const AddClientPage = lazy(() => import('views/pages/main/clientsPage/addClientPage/AddClientPage'));
const ProductPage = lazy(() => import('views/pages/main/catalog/productPage/ProductPage'));
const NotificationsPage = lazy(() => import('views/pages/main/notificationsPage'));
const WholesaleFeedPage = lazy(() => import('views/pages/main/wholesaleFeedPage'));
const CommercialProfilePage = lazy(() => import('views/pages/main/commercialProfilePage/CommercialProfilePage'));
const CatalogUploadsByUploadPage = lazy(() => import('views/pages/main/catalog/catalogUploads/catalogUploadsByUploadPage/CatalogUploadsByUploadPage'));
const CatalogUploadsBySeasonPage = lazy(() => import('views/pages/main/catalog/catalogUploads/catalogUploadsBySeasonPage/CatalogUploadsBySeasonPage'));
const AddCatalogImagesPagePreview = lazy(() => import('views/pages/main/catalog/addCatalogPageImagesPreview/AddCatalogImagesPagePreview'));
const CatalogCorrelation = lazy(() => import('views/pages/main/catalog/catalogCorrelation/CatalogCorrelation'));
const CatalogGeneric = lazy(() => import('views/pages/main/catalog/catalogGeneric/CatalogGeneric'));
const CatalogDownloadPage = lazy(() => import('views/pages/main/catalog/catalogDownloadPage/CatalogDownloadPage'));
const CatalogUpdateReferences = lazy(() => import('views/pages/main/catalog/catalogUpdateReferences/CatalogUpdateReferences'));
const CatalogTarifs = lazy(() => import('views/pages/main/catalog/catalogTarifs/CatalogTarifs'));
// Auth Pages
const SignupPage = lazy(() => import('views/pages/authentication/signupPage/SignupPage'));
const LoginRedirect = lazy(() => import('views/pages/authentication/loginRedirect/LoginRedirect'));
// Profile pages
const ProfilePage = lazy(() => import('views/pages/main/accountPage/profilePage/ProfilePage'));
const CommercialAgentsPage = lazy(() => import('views/pages/main/accountPage/commercialAgentsPage/CommercialAgentsPage'));
const CompanyDataPage = lazy(() => import('views/pages/main/accountPage/companyDataPage/CompanyDataPage'));
const MainContactsPage = lazy(() => import('views/pages/main/accountPage/mainContactsPage/MainContactsPage'));
// Client
const ClientResumePage = lazy(() => import('views/pages/main/clientPage/clientResumePage/ClientResumePage'));
const ClientPointsOfSalePage = lazy(() => import('views/pages/main/clientPage/clientPointsOfSalePage/ClientPointsOfSalePage'));
const ClientContactsPage = lazy(() => import('views/pages/main/clientPage/clientContactsPage/ClientContactsPage'))
const ClientBrandsSoldPage = lazy(() => import('views/pages/main/clientPage/clientBrandsSold/ClientBrandsSold'));
//Config
const SettingsPage = lazy(() => import('views/pages/main/settingsPage'));
const UserConfig = lazy(() => import('views/pages/main/settingsPage/usersPage/UsersPage'));
const EditApiConnection = lazy(() => import('views/pages/main/settingsPage/dataIntegrationPage/components/connections/components/editApiConnection/EditApiConnection'));
const ApiConfiguration = lazy(() => import('views/pages/main/settingsPage/dataIntegrationPage/components/connections/components/apiConfiguration/ApiConfiguration'));
const MapFieldsConfiguration = lazy(() => import('views/pages/main/settingsPage/dataIntegrationPage/components/connections/components/mapFieldsConfiguration/MapFieldsConfiguration'));
const MapConfiguration = lazy(() => import('views/pages/main/settingsPage/dataIntegrationPage/components/connections/components/mapConfiguration/MapConfiguration'));
const EditMap = lazy(() => import('views/pages/main/settingsPage/dataIntegrationPage/components/connections/components/editMap/EditMap'));
// Data Integration
const DataIntegrationPage = lazy(() => import('views/pages/main/settingsPage/dataIntegrationPage/DataIntegrationPage'));
const CreateUpdateMapping = lazy(() => import('views/pages/main/settingsPage/dataIntegrationPage/components/createUpdateMapping/CreateUpdateMapping'));
// Import file
const ImportFiles = lazy(() => import('views/pages/main/importPage/importFiles/ImportFiles'));
const ImportFilesHistory = lazy(() => import('views/pages/main/importPage/importFilesHistory/ImportFilesHistory'));
// Admin
// Sales
const SalesPage = lazy(() => import('views/pages/main/salesPage/SalesPage'));
const SalesMatching = lazy(() => import('views/pages/main/salesPage/salesMatching/SalesMatching'));
// Admin
const AdminHome = lazy(() => import('views/pages/admin/adminHome/AdminHome'));
const AdminAPIConfiguration = lazy(() => import('views/pages/admin/adminAPIConfiguration/AdminAPIConfiguration'));
const AdminDataIntegration = lazy(() => import('views/pages/admin/adminDataIntegration/AdminDataIntegration'));
// Error
const ErrorXXX = lazy(() => import('views/pages/messages/errorXXX'));
const ComingSoon = lazy(() => import('views/pages/messages/comingSoon/ComingSoon'));

const router = createBrowserRouter([
  {
    path: '/',
    element: (
      <LoginRequired>
        <Lazy>
          <MainLayout />
        </Lazy>
      </LoginRequired>
    ),
    children: [
      {
        path: '/',
        element: <Lazy><DashboardSalePage /></Lazy>,
      },
      {
        path: path.dashboardSale,
        element: <Lazy><DashboardSalePage /></Lazy>,
      },
      {
        path: path.dashboardStock,
        element: <Lazy><DashboardStockPage /></Lazy>,
      },
      {
        path: path.dashboardPrediction,
        element: <Lazy><DashboardPredictionPage /></Lazy>,
      },
      // { path: 'dashboard/my-brands', element: <Lazy><MyBrandsPage /></Lazy>},
      // CATALOG
      {
        path: `/${path.catalog}`,
        element: <Lazy><CatalogPage /></Lazy>,
      },
      {
        path: `${path.catalogProduct}/:productId`,
        element: <Lazy><ProductPage /></Lazy>,
      },
      {
        path: path.catalogUploads,
        element: <Lazy><CatalogUploadsLayout /></Lazy>,
        children: [
          {
            path: 'by-upload',
            element: <Lazy><CatalogUploadsByUploadPage /></Lazy>,
          },
          {
            path: 'by-season',
            element: <Lazy><CatalogUploadsBySeasonPage /></Lazy>,
          },
        ],
      },
      {
        path: 'catalog-uploads/add-catalog/upload-images',
        element: <Lazy><AddCatalogImagesPagePreview /></Lazy>,
      },
      {
        path: path.catalogCorrelation,
        element: <Lazy><CatalogCorrelation /></Lazy>,
      },
      {
        path: path.catalogGeneric,
        element: (
          <SuperAdminRequired>
            <Lazy><CatalogGeneric /></Lazy>
          </SuperAdminRequired>
        ),
      },
      {
        path: path.catalogDownload,
        element: <Lazy><CatalogDownloadPage /></Lazy>,
      },
      {
        path: path.catalogUpdateReferences,
        element: <Lazy><CatalogUpdateReferences /></Lazy>,
      },
       {
        path: path.catalogTarifs,
        element: <Lazy><CatalogTarifs /></Lazy>,
      },
      // MULTIMEDIA
      {
        path: 'multimedia',
        element: <Lazy><MultimediaPage /></Lazy>,
        children: [
          {
            path: 'videos',
            element: <Lazy><MultimediaVideosPage /></Lazy>,
          },
          {
            path: 'images',
            element: <Lazy><MultimediaImagesPage /></Lazy>,
          },
          {
            path: 'banners',
            element: <Lazy><MultimediaBannersPage /></Lazy>,
          },
          // { path: 'videos', element: <Lazy><ComingSoon /></Lazy> },
          // { path: 'images', element: <Lazy><ComingSoon /></Lazy> },
          // { path: 'banners', element: <Lazy><ComingSoon /></Lazy> },
        ],
      },
      // CLIENTS
      {
        path: path.clientsPath,
        element: <Lazy><ClientsPage /></Lazy>,
      },
      {
        path: path.pointsOfSalePath,
        element: <Lazy><ClientsPage /></Lazy>,
      },
      {
        path: 'add-client',
        element: <Lazy><AddClientPage /></Lazy>,
      },
      {
        path: path.client,
        element: <Lazy><ClientLayout /></Lazy>,
        children: [
          {
            path: `${path.clResume}/:clientId`,
            element: <Lazy><ClientResumePage /></Lazy>,
          },
          {
            path: `${path.clContacts}/:clientId`,
            element: <Lazy><ClientContactsPage /></Lazy>,
          },
          {
            path: `${path.clPointsOfSale}/:clientId`,
            element: <Lazy><ClientPointsOfSalePage /></Lazy>,
          },
          {
            path: `${path.clBrandsSold}/:clientId`,
            element: <Lazy><ClientBrandsSoldPage /></Lazy>,
          }
        ],
      },
       // SALES
      {
        path: path.sales,
        element: <Lazy><SalesPage /></Lazy>,
        children: [
          {
            path: path.salesMatching,
            element: <Lazy><SalesMatching /></Lazy>,
          }
        ],
      },
      // ACCOUNT
      {
        path: path.account,
        element: <Lazy><AccountLayout /></Lazy>,
        children: [
          {
            path: path.profile,
            element: <Lazy><ProfilePage /></Lazy>,
          },
          {
            path: path.company,
            element: <Lazy><CompanyDataPage /></Lazy>,
          },
          {
            path: path.contacts,
            element: <Lazy><MainContactsPage /></Lazy>,
          },
          {
            path: path.commercialAgents,
            element: <Lazy><CommercialAgentsPage /></Lazy>,
          },
          // { path: path.commercial, element: <Lazy><CommercialInformationPage /></Lazy> },
        ],
      },
      // IMPORT FILES
      {
        path: path.importFile,
        element: <Lazy><ImportFiles /></Lazy>,
      },
      {
        path: `${path.importFile}/:audienceId`,
        element: <Lazy><ImportFiles /></Lazy>,
      },
      {
        path: `${path.importHistory}`,
        element: <Lazy><ImportFilesHistory /></Lazy>,
      },
      // CONFIG
      {
        path: path.settings,
        element: <Lazy><SettingsPage /></Lazy>,
      },
      {
        path: path.settingsUsers,
        element: <Lazy><UserConfig /></Lazy>,
      },
      {
        path: path.settingsChronos,
        element: <Lazy><ComingSoon /></Lazy>,
      },
      {
        path: path.settingsDevices,
        element: <Lazy><ComingSoon /></Lazy>,
      },
      {
        path: path.settingsTouchScreen,
        element: <Lazy><ComingSoon /></Lazy>,
      },
      {
        path: path.settingsSystemSettings,
        element: <Lazy><ComingSoon /></Lazy>,
      },
      // {
      //   path: path.settingsMeta,
      //   element: <Lazy><MetaPage /></Lazy>,
      // },

      {
        path: `${path.settingsDataIntegrationEditAPI}/:connectorType/:connectorId`,
        element: <Lazy><EditApiConnection /></Lazy>,
      },
      {
        path: `${path.settingsDataIntegrationGeneralConfigurationAPI}/:connectorType`,
        element: <Lazy><ApiConfiguration /></Lazy>,
      },
      {
        path: path.settingsDataIntegrationMapFieldsConfigurationAPI,
        element: <Lazy><MapFieldsConfiguration /></Lazy>,
      },
      {
        path: path.settingsDataIntegrationMaps,
        element: <Lazy><MapConfiguration /></Lazy>,
      },
      {
        path: `${path.settingsDataIntegrationEditMap}/:mapId`,
        element: <Lazy><EditMap /></Lazy>,
      },
      {
        path: `${path.settingsDataIntegrationEditMap}/:mapId/:mapFieldId`,
        element: <Lazy><EditMap /></Lazy>,
      },
      {
        path: path.settingsCommercialProfile,
        element: <Lazy><CommercialProfilePage /></Lazy>,
      },
      {
        path: path.settingsBilling,
        element: <Lazy><ComingSoon /></Lazy>,
      },
      {
        path: path.settingsSupport,
        element: <Lazy><ComingSoon /></Lazy>,
      },
       {
        path: path.settingsDataIntegration,
        element: <Lazy><DataIntegrationPage /></Lazy>,
      },
       {
        path: path.settingsCreateUpdateMapping, // ?mapId=123&importToReprocessId=456
        element: <Lazy><CreateUpdateMapping /></Lazy>,
      },
      // NOTIFICATIONS
      {
        path: 'notifications',
        element: <Lazy><NotificationsPage /></Lazy>,
      },
      // WHOLESALE FEED
      {
        path: 'wholesale-feed/:wholesaleId',
        element: <Lazy><WholesaleFeedPage /></Lazy>,
      },
      // ERROR
      {
        path: '*',
        element: <Lazy><ErrorXXX code="404" /></Lazy>,
      },
    ],
  },
  {
    path: 'authentication',
    element: <Lazy><AuthenticationLayout /></Lazy>,
    children: [
      {
        path: 'signup',
        element: <Lazy><SignupPage /></Lazy>,
      },
      {
        path: 'signup-brand',
        element: <Lazy><RegisterBrand /></Lazy>,
      },
      {
        path: 'thanks',
        element: <Lazy><ThanksForRegister /></Lazy>,
      },
      {
        path: 'login',
        element: <Lazy><LoginRedirect /></Lazy>,
      },
    ],
  },

  {
    path: 'admin',
    element: (
      <SuperAdminRequired>
        <Lazy><AdminLayout /></Lazy>
      </SuperAdminRequired>
    ),
    children: [
      {
        path: 'home',
        element: <Lazy><AdminHome /></Lazy>,
      },
      {
        path: 'api-configuration',
        element: <Lazy><AdminAPIConfiguration /></Lazy>,
      },
      {
        path: `api-configuration/:mapFieldId`,
        element: <Lazy><AdminAPIConfiguration /></Lazy>,
      },
{
        path: 'data-integration',
        element: <Lazy><AdminDataIntegration /></Lazy>,
      },
    ],
  },
]);

function Lazy({ children }: IChildrenProps) {
  return <Suspense fallback={<></>}>{children}</Suspense>;
}

export default router;
